import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ToolbarItem } from '@components/PageHeaderToolbar/types';

export interface LayoutContextValue {
  title: string;
  toolbarItems: ToolbarItem[];

  handleTitleChange: Dispatch<SetStateAction<string>>;
  handleToolbarItemsChange: Dispatch<SetStateAction<ToolbarItem[]>>;

  isSpeedDialOpen: boolean;
  handleSpeedDialOpen: () => void;
  handleSpeedDialClose: () => void;
}

const initialValue: LayoutContextValue = {
  title: '',
  toolbarItems: [],

  handleTitleChange: () => undefined,
  handleToolbarItemsChange: () => undefined,

  isSpeedDialOpen: false,
  handleSpeedDialOpen: () => undefined,
  handleSpeedDialClose: () => undefined,
};

export const LayoutContext = createContext<LayoutContextValue>(initialValue);

export function LayoutProvider({ children }: PropsWithChildren) {
  const [title, setTitle] = useState<string>('');
  const [toolbarItems, setToolbarItems] = useState<ToolbarItem[]>([]);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);

  const handleSpeedDialOpen = useCallback(() => setIsSpeedDialOpen(true), []);
  const handleSpeedDialClose = useCallback(() => setIsSpeedDialOpen(false), []);

  const value = useMemo(
    () => ({
      title,
      toolbarItems,
      handleTitleChange: setTitle,
      handleToolbarItemsChange: setToolbarItems,
      isSpeedDialOpen,
      handleSpeedDialOpen,
      handleSpeedDialClose,
    }),
    [
      title,
      toolbarItems,
      isSpeedDialOpen,
      handleSpeedDialOpen,
      handleSpeedDialClose,
    ],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
